import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, UsersThree, X, PencilSimple, Trash, Users, ArrowClockwise } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { RoleBadge } from "@/components/RoleBadge";

const INV_STATUS = {
  pending: { label: "Ausstehend", color: "#F59E0B" },
  accepted: { label: "Angenommen", color: "#22C55E" },
  declined: { label: "Abgelehnt", color: "#EF4444" },
  expired: { label: "Abgelaufen", color: "#6B7280" },
};
const MEMBER_STATUS = {
  active: { label: "Aktiv", color: "#22C55E" },
  inactive: { label: "Inaktiv", color: "#6B7280" },
};

const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"; } catch { return "—"; } };

function Initials({ name, picture }) {
  if (picture) return <img src={picture} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-[var(--app-border)] shrink-0" />;
  const ini = (name || "?").trim().split(/\s+/).map((x) => x[0]).slice(0, 2).join("").toUpperCase();
  return <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-heading font-bold text-white shrink-0" style={{ background: "var(--app-primary)" }}>{ini || "?"}</div>;
}

export function PeopleManager({ variant = "profile" }) {
  const [invites, setInvites] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // { source, group_id, user_id, name, nickname, role, role_options }
  const [confirmRemove, setConfirmRemove] = useState(null); // { source, group_id, user_id, name }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [i, m] = await Promise.all([api.get("/invitations/outgoing"), api.get("/members/all")]);
      setInvites(i.data); setGroups(m.data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("sb:people-changed", onFocus);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); window.removeEventListener("sb:people-changed", onFocus); };
  }, [load]);

  const cancelInvite = async (inv) => {
    try { await api.post("/invitations/cancel", { source: inv.source, id: inv.id }); toast.success("Einladung zurückgezogen"); load(); window.dispatchEvent(new Event("sb:people-changed")); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const resendInvite = async (inv) => {
    try { await api.post("/invitations/resend", { source: inv.source, id: inv.id }); toast.success("Einladung erneut gesendet"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const roleOptionsFor = (grp) => grp.role_options || [];

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.post("/members/edit", { source: edit.source, group_id: edit.group_id, user_id: edit.user_id, nickname: edit.nickname, role: edit.role });
      toast.success("Mitglied aktualisiert"); setEdit(null); load(); window.dispatchEvent(new Event("sb:people-changed"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const doRemove = async () => {
    setBusy(true);
    try {
      await api.post("/members/remove", { source: confirmRemove.source, group_id: confirmRemove.group_id, user_id: confirmRemove.user_id });
      toast.success("Mitglied entfernt"); setConfirmRemove(null); load(); window.dispatchEvent(new Event("sb:people-changed"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const totalMembers = groups.reduce((s, g) => s + g.member_count, 0);
  const pendingByGroup = {};
  invites.forEach((iv) => { if (iv.can_cancel) pendingByGroup[iv.group_id] = (pendingByGroup[iv.group_id] || 0) + 1; });

  if (loading) return null;

  return (
    <div className="space-y-4" data-testid={`people-manager-${variant}`}>
      {/* Outgoing invitations */}
      <div className="sb-card p-5" data-testid="outgoing-invitations">
        <h2 className="text-lg font-heading font-semibold mb-1 flex items-center gap-2"><PaperPlaneTilt size={22} weight="duotone" style={{ color: "var(--app-strong)" }} />Gesendete Einladungen</h2>
        <p className="font-body text-xs text-gray-500 mb-3">Ausstehende und beantwortete Einladungen an Familie &amp; Gruppen.</p>
        {invites.length === 0 && <p className="font-body text-sm text-gray-500" data-testid="invites-empty">Keine gesendeten Einladungen.</p>}
        <div className="space-y-2">
          {invites.map((iv) => {
            const s = INV_STATUS[iv.status] || INV_STATUS.pending;
            return (
              <div key={`${iv.source}-${iv.id}`} data-testid={`invite-row-${iv.id}`} className="flex items-center justify-between gap-2 border-2 border-[var(--app-border)] rounded-2xl px-3 py-2.5 bg-[var(--app-surface)]">
                <div className="min-w-0">
                  <p className="font-body text-sm truncate">{iv.recipient || "—"}</p>
                  <p className="font-body text-[11px] text-gray-500 truncate">
                    {iv.source === "family" ? "👪 Familie" : `👥 ${iv.group_name}`} · {fmt(iv.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] uppercase px-2 py-0.5 rounded-full text-white" style={{ background: s.color }} data-testid={`invite-status-${iv.id}`}>{s.label}</span>
                  {iv.can_cancel && (
                    <button data-testid={`invite-resend-${iv.id}`} onClick={() => resendInvite(iv)} aria-label="Einladung erneut senden"
                      className="w-8 h-8 rounded-xl border-2 border-[var(--app-border)] bg-[var(--app-surface)] flex items-center justify-center active:scale-90 transition-transform" style={{ color: "var(--app-strong)" }}><ArrowClockwise size={15} weight="bold" /></button>
                  )}
                  {iv.can_cancel && (
                    <button data-testid={`invite-cancel-${iv.id}`} onClick={() => cancelInvite(iv)} aria-label="Einladung zurückziehen"
                      className="w-8 h-8 rounded-xl border-2 border-[var(--app-border)] bg-[var(--app-surface)] flex items-center justify-center text-red-500 active:scale-90 transition-transform"><X size={15} weight="bold" /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Members */}
      <div className="sb-card p-5" data-testid="members-manager">
        <h2 className="text-lg font-heading font-semibold mb-1 flex items-center gap-2"><Users size={22} weight="duotone" style={{ color: "var(--app-strong)" }} />Mitglieder</h2>
        <p className="font-body text-xs text-gray-500 mb-3">Alle Mitglieder deiner Familie &amp; Gruppen ({totalMembers}).</p>
        <div className="space-y-4">
          {groups.map((grp) => (
            <div key={`${grp.source}-${grp.group_id}`} data-testid={`member-group-${grp.group_id}`}>
              <div className="flex items-center gap-1.5 mb-2">
                {grp.source === "family" ? <UsersThree size={16} weight="bold" style={{ color: "var(--app-strong)" }} /> : <UsersThree size={16} weight="bold" className="text-gray-500" />}
                <span className="font-heading text-sm font-semibold">{grp.group_name}</span>
                <span className="font-body text-[11px] text-gray-400">· {grp.member_count}</span>
                {pendingByGroup[grp.group_id] > 0 && (
                  <span data-testid={`group-pending-${grp.group_id}`} className="text-[10px] font-heading px-2 py-0.5 rounded-full text-white" style={{ background: "#F59E0B" }} title="Offene Einladungen">{pendingByGroup[grp.group_id]} offen</span>
                )}
                {grp.kind === "event" && <span className="text-[10px] font-heading px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Event</span>}
              </div>
              <div className="space-y-2">
                {grp.members.length === 0 && <p className="font-body text-xs text-gray-400">Noch keine Mitglieder.</p>}
                {grp.members.map((m) => {
                  const st = MEMBER_STATUS[m.status] || MEMBER_STATUS.active;
                  return (
                    <div key={m.user_id} data-testid={`member-row-${grp.group_id}-${m.user_id}`} className="flex items-center gap-3 border-2 border-[var(--app-border)] rounded-2xl px-3 py-2 bg-[var(--app-surface)]">
                      <Initials name={m.nickname || m.name} picture={m.picture} />
                      <div className="min-w-0 flex-1">
                        <p className="font-body text-sm truncate">
                          {m.nickname || m.name}{m.is_you && " (Du)"}
                          {m.nickname && <span className="text-gray-400 font-body text-xs"> · {m.name}</span>}
                        </p>
                        <p className="font-body text-[11px] text-gray-500 truncate">
                          {m.joined_at ? `beigetreten ${fmt(m.joined_at)}` : ""}
                        </p>
                      </div>
                      <RoleBadge role={m.role} />
                      <span className="text-[10px] uppercase px-2 py-0.5 rounded-full text-white shrink-0" style={{ background: st.color }} data-testid={`member-status-${m.user_id}`}>{st.label}</span>
                      {m.editable && (
                        <button data-testid={`member-edit-${m.user_id}`} onClick={() => setEdit({ source: grp.source, group_id: grp.group_id, user_id: m.user_id, name: m.name, nickname: m.nickname || "", role: m.role, role_options: roleOptionsFor(grp) })}
                          className="w-8 h-8 rounded-xl border-2 border-[var(--app-border)] bg-[var(--app-surface)] flex items-center justify-center shrink-0 active:scale-90 transition-transform"><PencilSimple size={15} weight="bold" style={{ color: "var(--app-strong)" }} /></button>
                      )}
                      {m.removable && (
                        <button data-testid={`member-remove-${m.user_id}`} onClick={() => setConfirmRemove({ source: grp.source, group_id: grp.group_id, user_id: m.user_id, name: m.nickname || m.name })}
                          className="w-8 h-8 rounded-xl border-2 border-[var(--app-border)] bg-[var(--app-surface)] flex items-center justify-center text-red-500 shrink-0 active:scale-90 transition-transform"><Trash size={15} weight="bold" /></button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit member dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="member-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Mitglied bearbeiten</DialogTitle>
            <DialogDescription>{edit?.name}</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Gruppeninterner Name (Spitzname)</label>
                <input data-testid="member-nickname-input" className="sb-input mt-1" placeholder={edit.name} value={edit.nickname} onChange={(e) => setEdit({ ...edit, nickname: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Rolle</label>
                <select data-testid="member-role-select" className="sb-input mt-1" value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}>
                  {(edit.role_options || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <button data-testid="member-edit-save" onClick={saveEdit} disabled={busy} className="sb-btn w-full py-3 text-white font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>Speichern</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove confirm dialog */}
      <Dialog open={!!confirmRemove} onOpenChange={(o) => !o && setConfirmRemove(null)}>
        <DialogContent data-testid="member-remove-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Mitglied entfernen?</DialogTitle>
            <DialogDescription><strong>{confirmRemove?.name}</strong> wird aus dieser Gruppe entfernt. Diese Aktion kann nicht rückgängig gemacht werden.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <button data-testid="member-remove-cancel" onClick={() => setConfirmRemove(null)} className="sb-btn flex-1 py-2.5 bg-[var(--app-surface)] font-medium">Abbrechen</button>
            <button data-testid="member-remove-confirm" onClick={doRemove} disabled={busy} className="sb-btn flex-1 py-2.5 text-white font-medium disabled:opacity-50" style={{ background: "#EF4444" }}>Entfernen</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
